// Portfolio Diversification Checker -- the feature orchestrator.
//
// Same two-pane layout as the ETF Wealth Builder / Financial Freedom tools:
// the holdings form on the left, and on the right either an empty prompt, a
// processing state (while the ARQ job is polled) or the full results dashboard.

import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { useAnalyzeDiversification, useDiversificationResults } from './api/queries';
import { CapitalVsRiskChart } from './components/CapitalVsRiskChart';
import { ConversionCTA } from './components/ConversionCTA';
import { CorrelationHeatmap } from './components/CorrelationHeatmap';
import { InsightsPanel } from './components/InsightsPanel';
import { PcaScreeChart } from './components/PcaScreeChart';
import { PortfolioForm } from './components/PortfolioForm';
import { RiskDecompositionTable } from './components/RiskDecompositionTable';
import type { DiversificationResult, PortfolioRequest } from './types';

const SCORE_STYLES: Record<string, string> = {
    Excellent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Good: 'bg-sky-50 text-sky-700 border-sky-200',
    Moderate: 'bg-amber-50 text-amber-700 border-amber-200',
    Poor: 'bg-rose-50 text-rose-700 border-rose-200',
};

interface KpiProps {
    label: string;
    value: string;
    hint: string;
    accent: string;
    valueClass: string;
}

const KpiCard: React.FC<KpiProps> = ({ label, value, hint, accent, valueClass }) => (
    <div className={`bg-white p-5 rounded-2xl shadow-sm border border-slate-100 border-l-4 ${accent}`}>
        <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">{label}</p>
        <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
        <p className="text-xs text-slate-400 mt-1.5">{hint}</p>
    </div>
);

const ResultsView: React.FC<{ result: DiversificationResult }> = ({ result }) => {
    const scoreStyle = SCORE_STYLES[result.diversification_label] ?? SCORE_STYLES.Moderate;
    return (
        <div className="space-y-6 transition-opacity duration-500 opacity-100">
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">
                        Diversification Score
                    </p>
                    <div className="flex items-baseline gap-3">
                        <span className="text-4xl font-extrabold text-slate-900">
                            {result.diversification_score.toFixed(0)}
                        </span>
                        <span className="text-slate-400 font-semibold">/ 100</span>
                        <span className={`text-sm font-bold px-2.5 py-1 rounded-full border ${scoreStyle}`}>
                            {result.diversification_label}
                        </span>
                    </div>
                </div>
                <p className="text-xs text-slate-500 sm:text-right max-w-xs">
                    {result.observations} aligned trading days
                    {result.period_start && result.period_end
                        ? ` (${result.period_start} to ${result.period_end})`
                        : ''}
                    . {result.n_clusters} correlation cluster{result.n_clusters === 1 ? '' : 's'} detected.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <KpiCard
                    label="Portfolio Volatility"
                    value={`${(result.portfolio_volatility * 100).toFixed(2)}%`}
                    hint="Annualised standard deviation"
                    accent="border-l-slate-400"
                    valueClass="text-slate-800"
                />
                <KpiCard
                    label="Effective Bets (PCA)"
                    value={`${result.effective_number_of_bets.toFixed(2)} / ${result.tickers.length}`}
                    hint="Independent risk sources (Meucci)"
                    accent="border-l-sky-500"
                    valueClass="text-sky-600"
                />
                <KpiCard
                    label="Diversification Ratio"
                    value={`${result.diversification_ratio.toFixed(2)}x`}
                    hint="Weighted vol / portfolio vol"
                    accent="border-l-emerald-500"
                    valueClass="text-emerald-600"
                />
                <KpiCard
                    label="Risk HHI"
                    value={result.hhi_risk.toFixed(3)}
                    hint={`Capital HHI ${result.hhi_weight.toFixed(3)} (target < 0.15)`}
                    accent="border-l-amber-400"
                    valueClass={result.hhi_risk > 0.25 ? 'text-rose-500' : 'text-amber-500'}
                />
            </div>

            <InsightsPanel insights={result.insights} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <CapitalVsRiskChart rows={result.risk_decomposition} />
                <PcaScreeChart components={result.pca} />
            </div>

            <CorrelationHeatmap matrix={result.correlation_matrix} order={result.hrp_order} />
            <RiskDecompositionTable rows={result.risk_decomposition} />
            <ConversionCTA result={result} />
        </div>
    );
};

export const DiversificationDashboard: React.FC = () => {
    const queryClient = useQueryClient();
    const [jobId, setJobId] = useState<string | null>(null);
    const analyze = useAnalyzeDiversification();
    const results = useDiversificationResults(jobId);

    const handleSubmit = (payload: PortfolioRequest) => {
        analyze.mutate(payload, {
            onSuccess: (accepted) => {
                queryClient.removeQueries({ queryKey: ['diversificationResults', accepted.job_id] });
                setJobId(accepted.job_id);
            },
        });
    };

    const status = results.data?.status;
    const result = status === 'complete' ? results.data?.data : undefined;
    const isProcessing = analyze.isPending || results.isPolling;

    let jobError: string | null = null;
    if (status === 'failed') jobError = results.data?.error ?? 'The analysis failed.';
    else if (status === 'not_found') jobError = 'The analysis expired. Please run it again.';
    else if (results.isError) jobError = results.error.message;
    else if (results.isTimedOut) jobError = 'The analysis is taking longer than expected. Please try again.';

    const formError = analyze.error ?? (jobError && !isProcessing ? new Error(jobError) : null);

    return (
        <div className="max-w-7xl mx-auto p-4 lg:p-8 font-sans">
            <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
                    Portfolio Diversification Analyzer
                </h1>
                <p className="text-slate-600 dark:text-slate-300 max-w-3xl mx-auto text-lg">
                    Owning many positions does not mean you are diversified. This simple analyzer uses the methods
                    professionals rely on: risk decomposition (MCTR / PCTR), principal component
                    analysis to count your truly independent bets, and Hierarchical Risk Parity clustering to
                    reveal hidden correlations in your portfolio.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4 h-fit lg:sticky lg:top-8">
                    <PortfolioForm onSubmit={handleSubmit} isPending={isProcessing} error={formError} />
                </div>

                <div className="lg:col-span-8 flex flex-col">
                    {isProcessing ? (
                        <div className="h-full min-h-[500px] flex items-center justify-center bg-white rounded-2xl shadow-sm border border-slate-100">
                            <div className="flex flex-col items-center px-4 text-center">
                                <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                <span className="text-slate-700 font-semibold">Running quant engines...</span>
                                <span className="text-slate-400 text-sm mt-1">
                                    Loading price history, decomposing risk and clustering correlations.
                                </span>
                            </div>
                        </div>
                    ) : result ? (
                        <ResultsView result={result} />
                    ) : (
                        <div className="h-full min-h-[500px] flex items-center justify-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <p className="text-slate-400 font-medium px-4 text-center text-lg">
                                Enter your holdings and run the diversification check.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
